/**
 * Plot Drives per-column resource-code overrides (dev mode).
 *
 * Overrides are keyed by column key (col1, col4 …) and hold the resource code
 * picked from the templates API. They are persisted through the BOQ's
 * `groundworksItemLibrary.plotDrives[field].resourceCode` — no extra payload
 * field — and re-derived on load by comparing each saved code against the
 * code the column would resolve to without an override.
 */

import { EVALUATE_CODES } from './constants';
import { PLOT_DRIVES_COLUMN_MAPPING } from './plotDrivesColumnMapping';
import { PLOT_DRIVES_COL_CODES, PLOT_DRIVES_REFERENCES } from './plotDrivesReferenceMapping';

const CODE_PATTERN = /^(ALP|GWALP|ACL|ZEV|HAUL)[A-Z0-9]/i;

/** Code a column resolves to when no override is set (static, else from its dimension value). */
export function getDefaultPlotDrivesCode(
  colKey: string,
  dimensionValues: Record<string, string> = {},
): string | undefined {
  const dim = dimensionValues[colKey]?.trim();
  if (dim) {
    if (CODE_PATTERN.test(dim)) return dim.toUpperCase();
    const fromLabel = Object.values(EVALUATE_CODES).find(
      (e) => e.label.trim().toLowerCase() === dim.toLowerCase(),
    );
    if (fromLabel) return fromLabel.code;
  }
  const fieldName = PLOT_DRIVES_COLUMN_MAPPING[colKey];
  return PLOT_DRIVES_COL_CODES[colKey]?.code ?? (fieldName ? PLOT_DRIVES_REFERENCES[fieldName]?.code : undefined);
}

/**
 * Rebuild `{ colKey → code }` overrides from a saved `groundworksItemLibrary`.
 * Only codes that differ from the column's default are treated as overrides.
 */
export function deriveOverridesFromSavedLibrary(
  savedPlotDrivesLibrary: unknown,
  dimensionValues: Record<string, string> = {},
): Record<string, string> {
  const overrides: Record<string, string> = {};
  if (!savedPlotDrivesLibrary || typeof savedPlotDrivesLibrary !== 'object') return overrides;
  const library = savedPlotDrivesLibrary as Record<string, { resourceCode?: string }>;
  Object.entries(PLOT_DRIVES_COLUMN_MAPPING).forEach(([colKey, fieldName]) => {
    const saved = library[fieldName]?.resourceCode?.trim().toUpperCase();
    if (!saved) return;
    const def = getDefaultPlotDrivesCode(colKey, dimensionValues)?.toUpperCase();
    if (saved !== def) overrides[colKey] = saved;
  });
  return overrides;
}

/**
 * Minimal `groundworksItemLibrary.plotDrives` stand-in (`field → { resourceCode }`)
 * for the localStorage draft, which is written without item-library data. Lets
 * `deriveOverridesFromSavedLibrary` restore picks on a page reload.
 */
export function buildPlotDrivesLibraryFromOverrides(
  overrides: Record<string, string> | undefined,
): Record<string, { resourceCode: string }> | undefined {
  if (!overrides) return undefined;
  const library: Record<string, { resourceCode: string }> = {};
  Object.entries(overrides).forEach(([colKey, code]) => {
    const fieldName = PLOT_DRIVES_COLUMN_MAPPING[colKey];
    if (fieldName && code) library[fieldName] = { resourceCode: code };
  });
  return Object.keys(library).length > 0 ? library : undefined;
}
